import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

export const JobStatus = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Enhanced logging for duplicate detection flow tracking
    console.log(`[JobStatus] 🚀 Component mounted for job: ${jobId}`);
    console.log(`[JobStatus] This may be from: 1) New upload, 2) Duplicate detection, 3) Direct URL access`);
    
    const fetchJobStatus = async () => {
      try {
        console.log(`[JobStatus] 📡 Fetching status for job: ${jobId}`);
        const response = await axios.get(`/api/v1/jobs/${jobId}`);
        const jobData = response.data;
        
        // Log job details for debugging duplicate flow
        console.log(`[JobStatus] ✅ Job status received:`);
        console.log(`[JobStatus] - Status: ${jobData.status}`);
        console.log(`[JobStatus] - Created: ${jobData.created_at}`);
        console.log(`[JobStatus] - Progress: ${jobData.progress || 'N/A'}%`);
        
        // Special handling for different scenarios
        if (jobData.status === 'completed') {
          console.log(`[JobStatus] 🎉 Job completed - showing results (duplicate or fresh)`);
        } else if (jobData.status === 'processing') {
          console.log(`[JobStatus] ⏳ Job in progress - polling for updates (duplicate or fresh)`);
        } else if (jobData.status === 'failed') {
          console.log(`[JobStatus] ❌ Job failed - user can retry (duplicate detection provides second chance)`);
        } else if (jobData.status === 'queued') {
          console.log(`[JobStatus] 📋 Job queued - showing wait status (duplicate skips to front of queue mentally)`);
        }
        
        setJob(jobData);
        setLoading(false);
        return jobData;
      } catch (err) {
        console.error(`[JobStatus] ❌ Failed to fetch job status for ${jobId}:`, err);
        setError('Failed to fetch job status');
        setLoading(false);
        return null;
      }
    };

    // Initial fetch with enhanced logging
    console.log(`[JobStatus] 🔄 Starting initial status fetch...`);
    fetchJobStatus();

    // Poll for updates if job is still processing
    console.log(`[JobStatus] ⏲️ Setting up polling interval (5s) for real-time updates`);
    const interval = setInterval(async () => {
      console.log(`[JobStatus] 🔄 Polling update for job: ${jobId}`);
      const currentJob = await fetchJobStatus();
      
      // Stop polling if job is completed or failed
      if (currentJob && (currentJob.status === 'completed' || currentJob.status === 'failed')) {
        console.log(`[JobStatus] 🛑 Stopping polling - job reached final state: ${currentJob.status}`);
        clearInterval(interval);
      } else if (currentJob) {
        console.log(`[JobStatus] ⏳ Continuing to poll - job status: ${currentJob.status}`);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [jobId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      case 'processing':
        return <Clock className="w-6 h-6 text-blue-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'processing':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const downloadFile = async (fileType) => {
    try {
      const response = await axios.get(`/api/v1/jobs/${jobId}/download/${fileType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileType}_${jobId}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Link
          to="/"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Upload
        </Link>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Not Found</h2>
        <p className="text-gray-600 mb-4">The requested job could not be found.</p>
        <Link
          to="/"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Upload
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8">
        <Link
          to="/"
          className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 mr-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Job Status</h1>
      </div>

      {/* Job Info Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getStatusIcon(job.status)}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Job #{job.id.slice(0, 8)}
              </h2>
              <p className={`font-medium ${getStatusColor(job.status)}`}>
                {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
              </p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            <p>Created: {new Date(job.created_at).toLocaleString()}</p>
            {job.completed_at && (
              <p>Completed: {new Date(job.completed_at).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {job.status === 'processing' && job.progress !== null && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{Math.round(job.progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(job.progress, 100)}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Status Message */}
        {job.message && (
          <p className="text-gray-700 mb-4">{job.message}</p>
        )}

        {/* Error Message */}
        {job.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-medium">Error:</p>
            <p className="text-red-700">{job.error}</p>
          </div>
        )}
      </div>

      {/* Results */}
      {job.status === 'completed' && job.results && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Results</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Cartoon Style */}
            {job.results.cartoon_image && (
              <div className="text-center">
                <h4 className="font-medium text-gray-900 mb-2">Cartoon Style</h4>
                <div className="bg-gray-100 rounded-lg p-4 mb-2">
                  <img
                    src={`/api/v1/jobs/${jobId}/download/cartoon_style`}
                    alt="Cartoon Style"
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
                <button
                  onClick={() => downloadFile('cartoon_style')}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>
            )}

            {/* 3D Model */}
            {job.results.lego_image && (
              <div className="text-center">
                <h4 className="font-medium text-gray-900 mb-2">3D Model</h4>
                <div className="bg-gray-100 rounded-lg p-4 mb-2">
                  <img
                    src={`/api/v1/jobs/${jobId}/download/lego_minifigure`}
                    alt="3D Model"
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
                <button
                  onClick={() => downloadFile('lego_minifigure')}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>
            )}

            {/* Decal */}
            {job.results.decal_image && (
              <div className="text-center">
                <h4 className="font-medium text-gray-900 mb-2">Printable Decal</h4>
                <div className="bg-gray-100 rounded-lg p-4 mb-2">
                  <img
                    src={`/api/v1/jobs/${jobId}/download/decal`}
                    alt="Printable Decal"
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
                <button
                  onClick={() => downloadFile('decal')}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>
            )}
          </div>

          {/* Analysis Results */}
          <div className="mt-8">
            <h4 className="font-medium text-gray-900 mb-3">Analysis Results</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {job.results.r1_answers && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Basic Information</h5>
                  <div className="space-y-1">
                    {Object.entries(job.results.r1_answers).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {job.results.r2_answers && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Clothing Details</h5>
                  <div className="space-y-1">
                    {Object.entries(job.results.r2_answers).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {job.results.r3_answers && (
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Color Analysis</h5>
                  <div className="space-y-1">
                    {Object.entries(job.results.r3_answers).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center space-x-4">
        <Link
          to="/"
          className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Create New
        </Link>
        
        <Link
          to="/history"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          View History
        </Link>
      </div>
    </div>
  );
};
